class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onHeaderClick = this.onHeaderClick.bind(this);
    this.onHeaderKeydown = this.onHeaderKeydown.bind(this);
    this.updateHeaderAccessibility = this.updateHeaderAccessibility.bind(this);
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.classList.contains('active')) return;
      const inner = this.querySelector('.drawer__inner');
      const focusable = getFocusableElements(inner).filter((element) => element.getClientRects().length && !element.closest('[inert], [hidden]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        event.stopPropagation();
        (event.shiftKey ? last : first)?.focus();
      }
    });
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
      if (event.target.closest('[data-cart-retry]')) this.recoverCart();
      const protection = event.target.closest('[data-cart-protection-toggle]');
      // Expand the switch's tap area without capturing the coverage link or
      // forwarding a second click from the native label/checkbox.
      if (protection && !event.target.closest('a, input, label, button, select, textarea')) {
        const input = protection.querySelector('[data-cart-protection-input]');
        if (input && !input.disabled) input.click();
      }
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-protection-input]')) this.setProtection(event.target.checked);
    });
    this.addEventListener('submit', (event) => {
      if (this.isBusy || this.cartNeedsRefresh || this.readState()?.merchandise_count === 0) event.preventDefault();
    });
  }

  connectedCallback() {
    document.addEventListener('click', this.onHeaderClick);
    document.addEventListener('keydown', this.onHeaderKeydown);
    document.addEventListener('shopify:section:load', this.updateHeaderAccessibility);
    this.updateHeaderAccessibility();
    this.normalizeInitialCart();
    this.refreshCurrency();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onHeaderClick);
    document.removeEventListener('keydown', this.onHeaderKeydown);
    document.removeEventListener('shopify:section:load', this.updateHeaderAccessibility);
    if (this.classList.contains('active')) this.close();
  }

  onHeaderClick(event) {
    const link = event.target.closest('#cart-icon-bubble');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.open(link);
  }

  onHeaderKeydown(event) {
    if (event.code !== 'Space' || !event.target.closest('#cart-icon-bubble')) return;
    event.preventDefault();
    this.open(event.target.closest('#cart-icon-bubble'));
  }

  updateHeaderAccessibility() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'CartDrawer');
    link.setAttribute('aria-expanded', String(this.classList.contains('active')));
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.updateHeaderAccessibility();
    this.focusDrawer();
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble'));
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    this.updateHeaderAccessibility();
  }

  focusDrawer(focusId) {
    if (!this.classList.contains('active')) return;
    const inner = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : this.querySelector('.drawer__inner');
    if (!inner) return;
    const target = focusId && inner.querySelector(`#${CSS.escape(focusId)}`);
    trapFocus(inner, (target && !target.disabled && !target.closest('[inert]') && target.getClientRects().length ? target : null) || inner.querySelector('.drawer__close') || inner);
  }

  readState(root = this) {
    try {
      return JSON.parse(root.querySelector('[data-native-cart-state]')?.textContent || 'null');
    } catch {
      return null;
    }
  }

  refreshCurrency() {
    // BUCKS' documented integration for newly rendered .money elements.
    // Shopify money filters already supply the store's configured wrappers.
    try {
      window.bucksCC?.reConvert?.();
    } catch {
      // A display-converter failure must not interrupt cart or checkout updates.
    }
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  getRefreshSections() {
    const sections = new Map(this.getSectionsToRender().map((entry) => [entry.section, entry]));
    for (const entry of document.querySelector('cart-items')?.getSectionsToRender() || []) {
      sections.set(entry.section, entry);
    }
    return [...sections.values()];
  }

  async requestCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors || cart.status) {
      throw new Error(typeof cart.description === 'string' ? cart.description : window.cartStrings.error);
    }
    return cart;
  }

  async reconcileProtection(cart, sectionIds = this.getRefreshSections().map((entry) => entry.section)) {
    const state = this.readState();
    if (!state?.protectionEnabled || !Array.isArray(cart?.items)) return cart;
    const protection = cart.items.filter((item) => String(item.product_id) === state.protectionProductId);
    if (!protection.length) return cart;
    const hasMerchandise = cart.items.some((item) => String(item.product_id) !== state.protectionProductId && item.quantity > 0);
    const updates = {};
    protection.forEach((item, index) => {
      const quantity = hasMerchandise && index === 0 ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!Object.keys(updates).length) return cart;
    return this.requestCart(routes.cart_update_url, {
      updates,
      sections: [...new Set(sectionIds)],
      sections_url: window.location.pathname,
    });
  }

  async normalizeInitialCart() {
    const state = this.readState();
    if (!state?.protectionEnabled) return;
    this.setBusy(true);
    try {
      const cart = await this.reconcileProtection(state);
      if (cart !== state) {
        this.renderContents(cart, { open: false });
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-protection', cartData: cart });
      }
    } catch {
      this.cartNeedsRefresh = true;
      this.showProtectionError(window.cartStrings.error);
    } finally {
      this.setBusy(false);
    }
  }

  async setProtection(enabled) {
    if (this.cartNeedsRefresh) return this.recoverCart();
    const state = this.readState();
    if (!state?.protectionEnabled || this.isBusy || state.merchandise_count < 1) return;
    const protection = state.items.filter((item) => String(item.product_id) === state.protectionProductId);
    if (enabled && !state.protectionAvailable && !protection.length) return;
    this.setBusy(true);
    this.showProtectionError('');
    const sectionIds = this.getRefreshSections().map((entry) => entry.section);
    const context = { sections: sectionIds, sections_url: window.location.pathname };
    try {
      let cart;
      if (enabled && !protection.length) {
        const added = await this.requestCart(routes.cart_add_url, {
          ...context,
          items: [{ id: state.protectionVariantId, quantity: 1 }],
        });
        // add.js returns the added lines, not the complete cart required by subscribers.
        const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(window.cartStrings.error);
        cart = { ...(await response.json()), sections: added.sections };
        cart = await this.reconcileProtection(cart, sectionIds);
      } else {
        const updates = Object.fromEntries(protection.map((item, index) => [item.key, enabled && index === 0 ? 1 : 0]));
        cart = await this.requestCart(routes.cart_update_url, { ...context, updates });
      }
      this.renderContents(cart, { open: false, focusId: 'CartDrawer-Protection' });
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-protection', cartData: cart });
    } catch {
      // A failed response may still have changed the server cart. Read it back;
      // never blindly retry adding a paid item after a network/rendering failure.
      this.cartNeedsRefresh = true;
      try {
        await this.refreshContents({ focusId: 'CartDrawer-Protection' });
      } catch {
        const input = this.querySelector('[data-cart-protection-input]');
        if (input) input.checked = protection.length > 0;
      }
      this.showProtectionError(window.cartStrings.error);
    } finally {
      this.setBusy(false);
    }
  }

  async recoverCart() {
    if (this.isBusy) return;
    this.setBusy(true);
    try {
      const cart = await this.refreshContents();
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-protection', cartData: cart });
      this.showProtectionError('');
    } catch {
      this.cartNeedsRefresh = true;
      this.showProtectionError(window.cartStrings.error);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    const wasBusy = Boolean(this.isBusy);
    if (busy && !wasBusy) this.busyFocusId = this.contains(document.activeElement) ? document.activeElement.id : null;
    this.isBusy = busy;
    this.toggleAttribute('data-cart-busy', busy);
    this.setAttribute('aria-busy', String(busy));
    for (const items of document.querySelectorAll('cart-items, cart-drawer-items, .cart__dynamic-checkout-buttons')) {
      if (busy && !items.inert) {
        items.setAttribute('data-native-cart-inert', '');
        items.inert = true;
      } else if (!busy && items.hasAttribute('data-native-cart-inert')) {
        items.inert = false;
        items.removeAttribute('data-native-cart-inert');
      }
    }
    const input = this.querySelector('[data-cart-protection-input]');
    if (input) input.disabled = busy;
    for (const checkout of document.querySelectorAll('[name="checkout"]')) {
      if ((busy || this.cartNeedsRefresh) && !checkout.disabled) {
        checkout.setAttribute('data-native-cart-disabled', '');
        checkout.disabled = true;
      } else if (!busy && !this.cartNeedsRefresh && checkout.hasAttribute('data-native-cart-disabled')) {
        checkout.disabled = false;
        checkout.removeAttribute('data-native-cart-disabled');
      }
    }
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = busy || Boolean(this.cartNeedsRefresh) || this.readState()?.merchandise_count === 0;
    if (!busy && wasBusy) this.focusDrawer(this.busyFocusId);
  }

  showProtectionError(message) {
    const error = this.querySelector('#CartDrawer-ProtectionError');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
    const retry = this.querySelector('[data-cart-retry]');
    if (retry) {
      retry.hidden = !message || !this.cartNeedsRefresh;
      retry.disabled = retry.hidden;
    }
  }

  async refreshContents({ focusId } = {}) {
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getRefreshSections().map((entry) => entry.section).join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(window.cartStrings.error);
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error(window.cartStrings.error);
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const state = this.readState(html);
    if (!state) throw new Error(window.cartStrings.error);
    const cart = await this.reconcileProtection({ ...state, sections });
    this.renderContents(cart, { open: false, focusId });
    return cart;
  }

  renderContents(parsedState, { open = true, focusId } = {}) {
    const activeId = focusId || (this.contains(document.activeElement) ? document.activeElement.id : null);
    const scrollTop = this.querySelector('cart-drawer-items')?.scrollTop || 0;
    for (const section of this.getSectionsToRender()) {
      const html = parsedState.sections?.[section.section];
      if (!html) {
        if (section.section === 'cart-drawer') {
          if (open) {
            window.location.assign(routes.cart_url);
            return;
          }
          throw new Error(window.cartStrings.error);
        }
        continue;
      }
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      const target = section.id === 'cart-drawer' ? this.querySelector('#CartDrawer') : document.getElementById(section.id);
      if (!source || !target) {
        if (section.id === 'cart-drawer') throw new Error(window.cartStrings.error);
        continue;
      }
      target.innerHTML = source.innerHTML;
    }
    this.classList.toggle('is-empty', this.readState()?.merchandise_count === 0);
    this.cartNeedsRefresh = false;
    this.productId = parsedState.id;
    const items = this.querySelector('cart-drawer-items');
    if (items) items.scrollTop = scrollTop;
    this.setBusy(Boolean(this.isBusy));
    this.updateHeaderAccessibility();
    this.refreshCurrency();
    if (open && !this.classList.contains('active')) this.open();
    else this.focusDrawer(activeId);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
